import { useFirstItem } from '~/composables/useFirstItem';
import { useHasInitProfile } from '~/composables/useHasInitProfile';
import { useIntercom } from '~/composables/useIntercom';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useSupabaseClient } from '~/composables/useSupabaseClient';
import { useVerifiedSupabaseUser } from '~/composables/useVerifiedSupabaseUser';
import { useMerchantBySlugQuery } from '~/queries/useMerchantBySlugQuery';
import { useMerchantQuery } from '~/queries/useMerchantQuery';
import { useProfileQuery } from '~/queries/useProfileQuery';
import { useProgrammesQuery } from '~/queries/useProgrammesQuery';
import { usePublisherDataQuery } from '~/queries/usePublisherDataQuery';
import { usePublisherDataQuestionnaireQuery } from '~/queries/usePublisherDataQuestionnaireQuery';
import { intercomProfileSchema, productCategoriesToIntercomFormat, type IntercomProfile } from '~~/models/IntercomProfile';
import { getProgrammePayoutModalityValue } from '~~/models/PayoutModalities';
import { getPublisherIdForMerchant } from '~~/models/UserProfile';

export default defineNuxtPlugin({
	name: 'intercom',
	dependsOn: ['vue-query'],
	setup: () => {
		const isIntercomRunning = ref(false);
		const route = useRoute();
		const merchantSlug = useMerchantSlug(route);
		const user = useVerifiedSupabaseUser();
		const hasInitProfile = useHasInitProfile();

		const isLoggedInAndConfirmed = computed(() => !!user.value && user.value.is_anonymous !== true);

		const { data: publicMerchant } = useMerchantBySlugQuery({ merchantSlug });

		const merchantId = computed(() => publicMerchant.value?.id);

		const { data: merchant } = useMerchantQuery({ merchantId });
		const { boot, shutdown, setAppId, update } = useIntercom();

		const { data: profile } = useProfileQuery({ enabled: isLoggedInAndConfirmed, ignoreSentry: toRef(() => !hasInitProfile.value) });

		const publisherIdForMerchant = computed(() => merchant.value?.id && profile.value ? getPublisherIdForMerchant(profile.value, merchant.value.id) : undefined);
		const isSignedUpForMerchant = computed(() => !!publisherIdForMerchant.value);
		const isSignedUpForAnyMerchant = computed(() => profile.value ? Object.values(profile.value.publisherIds).some((id) => !!id) : false);

		const { data: questionnaireData } = usePublisherDataQuestionnaireQuery({ merchantId, enabled: isSignedUpForMerchant });
		const { data: publisherDataRaw } = usePublisherDataQuery({ enabled: isSignedUpForAnyMerchant });
		const { data: programmesData } = useProgrammesQuery({ merchantId, enabled: isSignedUpForMerchant });
		const publisherData = useFirstItem(publisherDataRaw);

		const firstProgramme = computed(() => programmesData.value ? programmesData.value[0] : undefined);

		const intercomProfile = computed<IntercomProfile | undefined>(() => {
			const parsedProfile = intercomProfileSchema.safeParse({
				app_id: merchant.value?.intercomAppId,
				email: user.value?.email,
				name: `${profile.value?.firstName} ${profile.value?.lastName}`,
				subdomain: merchant.value?.slug,
				user_id: publisherIdForMerchant.value ? String(publisherIdForMerchant.value) : undefined,
				cf_version: 'v2',
				country: publisherData.value?.country,
				commission: firstProgramme.value ? getProgrammePayoutModalityValue(firstProgramme.value.payout) : undefined,
			} satisfies Record<keyof IntercomProfile, unknown>);

			if (!parsedProfile.data) {
				return undefined;
			}

			const categories = questionnaireData.value && productCategoriesToIntercomFormat(questionnaireData.value.productCategories);

			return { ...parsedProfile.data, ...categories };
		});

		watch(
			intercomProfile,
			(newProfile, oldProfile) => {
				if (!newProfile) {
					if (isIntercomRunning.value) shutdown();
					return;
				}

				if (newProfile?.app_id === oldProfile?.app_id) {
					update(newProfile);
					return;
				}

				if (isIntercomRunning.value) {
					shutdown();
				}

				setAppId(newProfile.app_id);
				boot(newProfile);
				isIntercomRunning.value = true;
			},
			{ immediate: true },
		);

		const supabase = useSupabaseClient();

		supabase.auth.onAuthStateChange((state) => {
			if (state === 'SIGNED_OUT' && isIntercomRunning.value) {
				shutdown();
			}
		});
	},
});
